import { useState, useEffect, useCallback } from 'react';
import { formatFranc } from '../lib/utils';
import { Virtuoso } from 'react-virtuoso';
import { entityAPI, leagueAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import BackButton from '../components/BackButton';
import EntityAvatar from '../components/EntityAvatar';
import { Search, Check, X, CheckSquare, Square, Users } from 'lucide-react';
import { toast } from 'sonner';

// Configurable: multiplier for minimum people pool per slot
// Formula: minEntities = maxPlayers × slotsPerCategory × ENTITY_POOL_MULTIPLIER
// Set to 1.5 (industry standard) for 50% variety buffer - balances competition and choice
const ENTITY_POOL_MULTIPLIER = 1.5;

const ENTITY_TYPES = [
  { value: 'country_leader', label: 'Rulers' },
  { value: 'ceo', label: 'CEOs' },
  { value: 'actor', label: 'Actors' },
  { value: 'athlete', label: 'Athletes' },
  { value: 'musician', label: 'Artists' },
  { value: 'influencer', label: 'Influencers' },
];

export default function LeagueEntitySelector({ leagueId, slotConfig, maxPlayers, onDone, onBack }) {
  const [entities, setEntities] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // 2026-08-15 (pool editing) — effective league context. Route props are
  // set by the just-created flow; when reopened later from "My Leagues"
  // they're null and GET /leagues/{id}/pool supplies them.
  const [cfg, setCfg] = useState(slotConfig || null);
  const [maxP, setMaxP] = useState(maxPlayers || null);
  const [isEdit, setIsEdit] = useState(false);

  // Load league pool context + all active entities
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Resolve the CURRENT pool first so reopening the checklist
        // pre-checks what's saved — not "everyone".
        let effCfg = slotConfig || null;
        let effMax = maxPlayers || null;
        let existing = [];
        try {
          const poolRes = await leagueAPI.pool(leagueId);
          existing = poolRes.data?.allowed_entity_ids || [];
          if (!effCfg) effCfg = poolRes.data?.slot_config || {};
          if (!effMax) effMax = poolRes.data?.max_members || 2;
        } catch (poolErr) {
          effCfg = effCfg || {};
          effMax = effMax || 2;
        }
        if (cancelled) return;
        setCfg(effCfg);
        setMaxP(effMax);
        setIsEdit(existing.length > 0);

        // 2026-08-15 (owner request: faster picker) — ONE request via the
        // dedicated /entities/picker feed (minimal projection of every
        // active person) instead of 8+ sequential 200-row pages (~5s → <1s).
        const res = await entityAPI.picker();
        const all = res.data.entities || [];
        if (cancelled) return;
        // 2026-08-15 (testing-agent) — restrict the pool to categories the
        // league actually enables (slot_config > 0). People from disabled
        // categories can never be drafted here, so listing + pre-checking
        // all 1600+ of them was pure noise.
        const enabled = new Set(
          Object.entries(effCfg || {}).filter(([, v]) => v > 0).map(([k]) => k)
        );
        const pool = enabled.size > 0 ? all.filter((e) => enabled.has(e.entity_type)) : all;
        setEntities(pool);
        // Pre-check the saved pool when editing; default select-all on create.
        if (existing.length > 0) {
          const existingSet = new Set(existing);
          setSelectedIds(new Set(pool.filter((e) => existingSet.has(e.id)).map((e) => e.id)));
        } else {
          setSelectedIds(new Set(pool.map((e) => e.id)));
        }
      } catch (e) {
        if (!cancelled) toast.error('Could not load people. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  // Calculate minimum required entities per category
  const getMinForCategory = (cat) => {
    const slotsForCat = cfg?.[cat] || 0;
    if (slotsForCat === 0) return 0;
    
    const idealMin = slotsForCat * (maxP || 2) * ENTITY_POOL_MULTIPLIER;
    
    // FIX: Cap minimum to actual available entities in this category
    const availableCount = entities.filter(e => e.entity_type === cat).length;
    
    // Use the lesser of ideal minimum or available count
    return Math.min(idealMin, availableCount);
  };

  const totalSlotsNeeded = Object.values(cfg || {}).reduce((a, b) => a + b, 0);
  const totalMinEntities = Object.entries(cfg || {}).reduce((sum, [cat, slots]) => {
    return sum + (slots > 0 ? slots * (maxP || 2) * ENTITY_POOL_MULTIPLIER : 0);
  }, 0);

  // Count selected per category
  const selectedByCategory = {};
  entities.forEach(e => {
    if (selectedIds.has(e.id)) {
      selectedByCategory[e.entity_type] = (selectedByCategory[e.entity_type] || 0) + 1;
    }
  });

  const toggleEntity = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllInCategory = (cat) => {
    const catIds = entities.filter(e => e.entity_type === cat).map(e => e.id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      catIds.forEach(id => next.add(id));
      return next;
    });
  };

  const deselectAllInCategory = (cat) => {
    const catIds = new Set(entities.filter(e => e.entity_type === cat).map(e => e.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      catIds.forEach(id => next.delete(id));
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(entities.map(e => e.id)));
  const deselectAll = () => setSelectedIds(new Set());

  // Validate minimums
  const isValid = () => {
    for (const [cat, slots] of Object.entries(cfg || {})) {
      if (slots > 0) {
        const min = getMinForCategory(cat);
        const count = selectedByCategory[cat] || 0;
        if (count < min) return false;
      }
    }
    return selectedIds.size > 0;
  };

  const handleSave = async () => {
    if (!isValid()) {
      toast.error('Not enough people selected. Check minimum requirements per category.');
      return;
    }
    setSaving(true);
    try {
      await leagueAPI.update(leagueId, { allowed_entity_ids: [...selectedIds] });
      toast.success(`League people pool ${isEdit ? 'updated' : 'set'}: ${selectedIds.size} people`);
      // 2026-08-15 — let MoreTab / App refresh "custom pool: N" badges live.
      window.dispatchEvent(new CustomEvent('fws:league-pool-updated', {
        detail: { leagueId, poolSize: selectedIds.size },
      }));
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  // Filter entities
  const filtered = entities.filter(e => {
    if (categoryFilter && e.entity_type !== categoryFilter) return false;
    if (search && !e.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="p-4"><Skeleton className="h-60 w-full" /></div>;

  return (
    <div className="p-4" data-testid="league-entity-selector">
      <BackButton onClick={onBack} />
      <h2 className="text-base font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
        {isEdit ? 'Edit League People' : 'Select League People'}
      </h2>
      <p className="text-xs text-muted-foreground mb-3">
        Choose which people are available in this league. Selected: {selectedIds.size} / {entities.length}
      </p>

      {/* Category requirements */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {ENTITY_TYPES.filter(t => (cfg?.[t.value] || 0) > 0).map(t => {
          const count = selectedByCategory[t.value] || 0;
          const min = getMinForCategory(t.value);
          const availableCount = entities.filter(e => e.entity_type === t.value).length;
          const idealMin = (cfg?.[t.value] || 0) * (maxP || 2) * ENTITY_POOL_MULTIPLIER;
          const isCapped = idealMin > availableCount;
          const ok = count >= min;
          
          return (
            <Badge 
              key={t.value} 
              variant={ok ? 'default' : 'destructive'} 
              className="text-[9px]"
              title={isCapped ? `Only ${availableCount} ${t.label} available in game` : ''}
            >
              {t.label}: {count}/{min} min
              {isCapped && <span className="ml-1 opacity-60">(max available)</span>}
            </Badge>
          );
        })}
      </div>

      {/* Bulk actions */}
      <div className="flex gap-2 mb-3">
        <Button size="sm" variant="outline" className="text-xs" onClick={selectAll} data-testid="pool-select-all-btn">Select All</Button>
        <Button size="sm" variant="outline" className="text-xs" onClick={deselectAll} data-testid="pool-deselect-all-btn">Deselect All</Button>
      </div>

      {/* Search + Category filter */}
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
        </div>
      </div>

      <div className="flex gap-1 mb-3 overflow-x-auto">
        <button onClick={() => setCategoryFilter('')} className={`shrink-0 px-2 py-1 text-[10px] rounded-full border ${!categoryFilter ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>All</button>
        {ENTITY_TYPES.filter(t => (cfg?.[t.value] || 0) > 0).map(t => (
          <button key={t.value} onClick={() => setCategoryFilter(t.value)}
            className={`shrink-0 px-2 py-1 text-[10px] rounded-full border flex items-center gap-1 ${categoryFilter === t.value ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
            {t.label}
            <span className="font-bold">{selectedByCategory[t.value] || 0}</span>
          </button>
        ))}
      </div>

      {/* Per-category bulk select */}
      {categoryFilter && (
        <div className="flex gap-2 mb-2">
          <Button size="sm" variant="ghost" className="text-[10px] h-6" onClick={() => selectAllInCategory(categoryFilter)} data-testid="pool-select-all-category-btn">
            <CheckSquare className="h-3 w-3 mr-1" /> Select all {ENTITY_TYPES.find(t => t.value === categoryFilter)?.label}
          </Button>
          <Button size="sm" variant="ghost" className="text-[10px] h-6" onClick={() => deselectAllInCategory(categoryFilter)}>
            <Square className="h-3 w-3 mr-1" /> Deselect all
          </Button>
        </div>
      )}

      {/* Entity list — virtualized (react-virtuoso, 2026-08-15): renders only
          visible rows so a 1,600-person checklist scrolls smoothly. */}
      <div data-testid="pool-entity-list">
        <Virtuoso
          style={{ height: 'calc(100vh - 380px)', minHeight: 240 }}
          data={filtered}
          computeItemKey={(_, entity) => entity.id}
          itemContent={(_, entity) => {
            const selected = selectedIds.has(entity.id);
            return (
              <div
                className={`flex items-center gap-2 px-3 py-2 mb-1 rounded-lg border cursor-pointer transition-colors ${selected ? 'bg-primary/5 border-primary/30' : 'bg-card border-border/40 opacity-80'}`}
                onClick={() => toggleEntity(entity.id)}
                data-testid={`pool-entity-${entity.id}`}
                aria-checked={selected}
                role="checkbox"
              >
                <div className={`h-5 w-5 rounded flex items-center justify-center shrink-0 ${selected ? 'bg-primary text-primary-foreground' : 'border border-border'}`}>
                  {selected && <Check className="h-3 w-3" />}
                </div>
                <EntityAvatar entity={entity} size={28} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{entity.name}</p>
                  <p className="text-[9px] text-muted-foreground">{entity.entity_type?.replace(/_/g, ' ')}</p>
                </div>
                <span className="text-[10px] tabular-nums shrink-0">{formatFranc(entity.pricing?.price)}</span>
              </div>
            );
          }}
        />
      </div>

      {/* Save button — sticky above the bottom tab bar so it's never
          clipped by the viewport (testing-agent iteration_178). */}
      <div className="sticky bottom-20 z-10 mt-3 bg-background/95 backdrop-blur-sm pt-2 pb-1 rounded-lg">
        <Button className="w-full" onClick={handleSave} disabled={saving || !isValid()} data-testid="pool-save-btn">
          {saving ? 'Saving...' : `Confirm People Pool (${selectedIds.size} people)`}
        </Button>
      </div>
    </div>
  );
}
